import React, { Component } from "react";
import { createPortal } from "react-dom";
import Tagify from "@yaireo/tagify";
import "@yaireo/tagify/dist/tagify.css";
import {
  Plus,
  ChevronLeft,
  Building,
  MapPin,
  Camera,
  MoreVertical,
  Loader2,
  AlertCircle,
  CheckCircle,
  Trash2,
  Check,
  Search,
} from "lucide-react";

import axios from "../../utils/axiosInstance";
import "./MyListing.css";

class MyListing extends Component {
  constructor(props) {
    super(props);

    this.fileInputRef = React.createRef();
    this.galleryInputRef = React.createRef();
    this.detailsScrollRef = React.createRef();
    this.facilitysInputRef = React.createRef();

    this.facilityTagify = null;

    this.state = {
      step: 0,
      activeTab: "All",
      properties: [],
      loadingProperties: false,
      showActionMenu: null,
      editMode: false,
      editPostId: null,

      /* Categories */
      categories: [],
      loadingCategories: false,
      category_id: "",

      /* Facilities */
      facilities: [],
      facility_ids: "",
      showFacilityPopup: false,
      facilitySearchQuery: "",

      /* Basic Property Details */
      property_type_id: "",
      title: "",
      content: "",
      sales_price: "",
      checkin_time: "",
      checkout_time: "",
      room_number: 0,
      beds: 0,
      baths: 0,
      adults: 0,
      children: 0,
      real_address: "",
      map_latitude: "",
      map_longitude: "",

      /* Advanced Tab New Details */
      formTab: "Basic",
      minimum_night: "",
      extra_charge_on_additional_guest_after: "",
      per_guest_charge: "",
      weekend_price: "",
      booking_status: "0",
      post_state: "1",

      /* Dynamic Discount More Night Popup Details */
      showDiscountPopup: false,
      dynamic_discounts: [
        { id: Date.now() + 1, label: "2-night plus discount", value: "" },
        { id: Date.now() + 2, label: "Weekly discount", value: "" },
        { id: Date.now() + 3, label: "Monthly discount", value: "" },
      ],

      /* Images */
      feature_image: null,
      gallery_images: [],
      imagePreview: null,
      galleryPreviews: [],
      existingGallery: [],

      /* UI States */
      submitting: false,
      submitSuccess: false,
      errorMessage: "",
    };

    // Shared Brand Theme Color
    this.brandColor = "#0049b0"; 
    
    this.containerStyle = {
      maxWidth: "450px",
      marginBottom: "30px",
    };
    this.bottomNavOffset = "65px";

    const baseUrl = axios.defaults.baseURL
      ? axios.defaults.baseURL.replace("/api", "")
      : "";
    this.backendUploadsPath = `${baseUrl}/uploads/posts/`;
  }

  componentDidMount() {
    const web_user_id =
      localStorage.getItem("web_user_id") ||
      sessionStorage.getItem("web_user_id");
    if (
      !web_user_id ||
      web_user_id === "null" ||
      web_user_id === "undefined" ||
      String(web_user_id).trim() === ""
    ) {
      window.location.replace("/searchresultmobile");
      return;
    }

    this.fetchCategories();
    this.fetchFacilities();
    this.fetchProperties();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.showDiscountPopup !== this.state.showDiscountPopup || prevState.showFacilityPopup !== this.state.showFacilityPopup) {
      document.body.style.overflow = (this.state.showDiscountPopup || this.state.showFacilityPopup) ? "hidden" : "";
    }

    // Clean up Tagify instance when switching away from Advanced tab
    if (prevState.formTab === "Advanced" && this.state.formTab !== "Advanced") {
      if (this.facilityTagify) {
        this.facilityTagify.destroy();
        this.facilityTagify = null;
      }
    }

    // Re-initialize Tagify when entering Advanced tab or when facilities are loaded
    if (
      (prevState.facilities !== this.state.facilities ||
       prevState.formTab !== this.state.formTab ||
       prevState.step !== this.state.step) &&
      this.state.formTab === "Advanced" &&
      this.state.step === 6
    ) {
      setTimeout(() => {
        this.initFacilityTagify();
      }, 50);
    }
  }

  componentWillUnmount() {
    document.body.style.overflow = "";
    if (this.facilityTagify) this.facilityTagify.destroy();
    if (this.state.imagePreview) URL.revokeObjectURL(this.state.imagePreview);
    this.state.galleryPreviews.forEach((preview) =>
      URL.revokeObjectURL(preview.url)
    );
  }

  /* API FETCHING METHODS */
  fetchCategories = async () => {
    try {
      this.setState({ loadingCategories: true, errorMessage: "" });
      const response = await axios.get("/get_category");
      const data = response?.data?.data || response?.data || [];
      this.setState({ categories: Array.isArray(data) ? data : [] });
    } catch (error) {
      console.error("Category loading error:", error);
      this.setState({
        categories: [],
        errorMessage: "Unable to load property categories.",
      });
    } finally {
      this.setState({ loadingCategories: false });
    }
  };

  fetchFacilities = async () => {
    try {
      const response = await axios.get("/facilities");
      const data = response?.data?.data || response?.data || [];
      this.setState({ facilities: Array.isArray(data) ? data : [] });
    } catch (error) {
      console.error("Facilities loading error:", error);
      this.setState({ facilities: [] });
    }
  };

  fetchProperties = async () => {
    try {
      this.setState({ loadingProperties: true });
      const webUserId = this.getWebUserId();
      const response = await axios.get(`/my_listing`, {
        params: { web_user_id: webUserId },
      });
      const data = response?.data?.data || response?.data || [];
      this.setState({ properties: Array.isArray(data) ? data : [] });
    } catch (error) {
      console.error("My listings loading error:", error);
      this.setState({ properties: [] });
    } finally {
      this.setState({ loadingProperties: false });
    }
  };

  /* TAGIFY INITIALIZATION FOR FACILITIES */
  initFacilityTagify = () => {
    if (!this.facilitysInputRef.current) return;

    const facilityWhitelist = this.state.facilities
      .map((f) => (typeof f === "string" ? f : f?.facility_name || f?.name || f?.value))
      .filter(Boolean);

    if (this.facilityTagify) {
      this.facilityTagify.settings.whitelist = facilityWhitelist;
    } else {
      this.facilityTagify = new Tagify(this.facilitysInputRef.current, {
        whitelist: facilityWhitelist,
        maxTags: 3000,
        dropdown: {
          maxItems: 3000,
          classname: "tags-look",
          enabled: 0,
          closeOnSelect: false,
        },
      });

      this.facilityTagify.on("change", (e) => {
        let facilitys = [];
        if (e.detail.value) {
          try {
            facilitys = JSON.parse(e.detail.value).map((t) => t.value);
          } catch (err) {
            console.error("Tagify parse error:", err);
          }
        }
        this.setState({ facility_ids: facilitys.join(",") });
      });
    }

    if (this.facilityTagify && this.state.facility_ids) {
      this.facilityTagify.removeAllTags();
      const facilitysToAdd = typeof this.state.facility_ids === "string"
        ? this.state.facility_ids.split(",").filter(Boolean)
        : this.state.facility_ids;
      if (facilitysToAdd && facilitysToAdd.length > 0) {
        this.facilityTagify.addTags(facilitysToAdd);
      }
    }
  };

  /* EDIT & STATE HANDLERS */
  handleEditInit = async (id) => {
    this.setState({ showActionMenu: null, loadingProperties: true });
    try {
      const response = await axios.get(`/post_listing_by_id`, {
        params: { id },
      });
      const item = response?.data?.data?.[0];

      if (item) {
        let parsedGallery = [];
        if (item.gallery_images) {
          try {
            parsedGallery = JSON.parse(item.gallery_images);
          } catch (e) {
            console.error("Gallery parse err", e);
          }
        }

        let parsedDiscount = [];
        if (item.more_night_discount) {
          try {
            const rawDiscount = JSON.parse(item.more_night_discount);
            if (Array.isArray(rawDiscount)) {
              parsedDiscount = rawDiscount;
            } else if (typeof rawDiscount === "object") {
              parsedDiscount = [
                { id: Date.now() + 1, label: "2-night plus discount", value: rawDiscount["2_night"] || "" },
                { id: Date.now() + 2, label: "Weekly discount", value: rawDiscount["weekly"] || "" },
                { id: Date.now() + 3, label: "Monthly discount", value: rawDiscount["monthly"] || "" }
              ];
            }
          } catch (e) {
            console.error("Discount Parse Error", e);
          }
        }

        if (parsedDiscount.length === 0) {
          parsedDiscount = [
            { id: Date.now() + 1, label: "2-night plus discount", value: "" },
            { id: Date.now() + 2, label: "Weekly discount", value: "" },
            { id: Date.now() + 3, label: "Monthly discount", value: "" }
          ];
        }

        this.setState({
          editMode: true,
          editPostId: item.id,
          category_id: item.category_id || "",
          facility_ids: item.facility_ids || "",
          property_type_id: item.property_type_id || item.property_type || "",
          title: item.title || "",
          content: item.content || "",
          sales_price: item.sales_price || item.regular_price || "",
          checkin_time: item.checkin_time || "",
          checkout_time: item.checkout_time || "",
          room_number: item.room_number || 0,
          beds: item.beds || 0,
          baths: item.baths || 0,
          adults: item.adults || 0,
          children: item.children || 0,
          real_address: item.real_address || "",
          map_latitude: item.map_latitude || "",
          map_longitude: item.map_longitude || "",

          formTab: "Basic",
          minimum_night: item.minimum_night || "",
          extra_charge_on_additional_guest_after: item.extra_charge_on_additional_guest_after || "",
          per_guest_charge: item.per_guest_charge || "",
          weekend_price: item.weekend_price || "",
          booking_status: item.booking_status || "0",
          post_state: item.post_state || "1",

          dynamic_discounts: parsedDiscount,

          imagePreview: item.feature_image
            ? `${this.backendUploadsPath}${item.feature_image}`
            : null,
          existingGallery: parsedGallery.map(
            (img) => `${this.backendUploadsPath}${img}`
          ),

          feature_image: null,
          gallery_images: [],
          galleryPreviews: [],

          step: 4,
          errorMessage: "",
        });
      }
    } catch (error) {
      console.error("Fetch single post error:", error);
      this.setState({ errorMessage: "Failed to load listing details." });
    } finally {
      this.setState({ loadingProperties: false });
    }
  };

  handleExit = () => {
    if (this.facilityTagify) {
      this.facilityTagify.destroy();
      this.facilityTagify = null;
    }

    this.setState({
      step: 0,
      editMode: false,
      editPostId: null,
      submitSuccess: false,
      errorMessage: "",
      
      category_id: "",
      facility_ids: "",
      property_type_id: "",
      title: "",
      content: "",
      sales_price: "",
      checkin_time: "",
      checkout_time: "",
      room_number: 0,
      beds: 0,
      baths: 0,
      adults: 0,
      children: 0,
      real_address: "",
      map_latitude: "",
      map_longitude: "",

      formTab: "Basic",
      minimum_night: "",
      extra_charge_on_additional_guest_after: "",
      per_guest_charge: "",
      weekend_price: "",
      booking_status: "0",
      post_state: "1",
      showDiscountPopup: false,
      showFacilityPopup: false,
      facilitySearchQuery: "",
      
      dynamic_discounts: [
        { id: Date.now() + 1, label: "2-night plus discount", value: "" },
        { id: Date.now() + 2, label: "Weekly discount", value: "" },
        { id: Date.now() + 3, label: "Monthly discount", value: "" },
      ],

      feature_image: null,
      gallery_images: [],
      imagePreview: null,
      galleryPreviews: [],
      existingGallery: [],
    });
  };

  handleChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value, errorMessage: "", submitSuccess: false });
  };

  /* DYNAMIC DISCOUNT HANDLERS */
  handleAddDiscount = () => {
    this.setState((prevState) => ({
      dynamic_discounts: [
        ...prevState.dynamic_discounts,
        { id: Date.now(), label: "", value: "" },
      ],
    }));
  };

  handleRemoveDiscount = (idToRemove) => {
    this.setState((prevState) => ({
      dynamic_discounts: prevState.dynamic_discounts.filter(
        (discount) => discount.id !== idToRemove
      ),
    }));
  };

  handleDiscountItemChange = (id, field, newValue) => {
    this.setState((prevState) => ({
      dynamic_discounts: prevState.dynamic_discounts.map((discount) =>
        discount.id === id ? { ...discount, [field]: newValue } : discount
      ),
    }));
  };

  updateCounter = (key, delta) => {
    this.setState((prevState) => {
      let newValue = Number(prevState[key] || 0) + delta;
      newValue = Math.max(0, newValue);
      newValue = Math.round(newValue * 10) / 10;
      return { [key]: newValue };
    });
  };

  getCurrentLocation = () => {
    if (navigator.geolocation) {
      this.setState({ errorMessage: "" });
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.setState({
            map_latitude: position.coords.latitude.toString(),
            map_longitude: position.coords.longitude.toString(),
          });
        },
        (error) => {
          let errorMsg = "Unable to retrieve your location.";
          if (error.code === error.PERMISSION_DENIED) {
            errorMsg =
              "Location permission was denied. Please allow it in your settings.";
          }
          this.setState({ errorMessage: errorMsg });
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
      );
    } else {
      this.setState({
        errorMessage: "Geolocation is not supported by this device/browser.",
      });
    }
  };

  /* IMAGE UPLOAD HANDLERS */
  handleFeatureImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (this.state.imagePreview && !this.state.editMode) {
      URL.revokeObjectURL(this.state.imagePreview);
    }

    this.setState({
      feature_image: file,
      imagePreview: URL.createObjectURL(file),
      errorMessage: "",
    });
  };

  handleGalleryImagesChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const previews = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));

    this.setState((prevState) => ({
      gallery_images: [...prevState.gallery_images, ...files],
      galleryPreviews: [...prevState.galleryPreviews, ...previews],
      errorMessage: "",
    }));
  };

  removeGalleryImage = (index) => {
    this.setState((prevState) => {
      URL.revokeObjectURL(prevState.galleryPreviews[index].url);
      return {
        gallery_images: prevState.gallery_images.filter((_, i) => i !== index),
        galleryPreviews: prevState.galleryPreviews.filter((_, i) => i !== index),
      };
    });
  };

  /* FORM SUBMISSION & VALIDATION */
  validateListing = () => {
    const s = this.state;
    if (!s.category_id) return "Please select a property category.";
    if (!s.property_type_id || !["Entire Place", "Private Room"].includes(String(s.property_type_id))) {
      return "Please select Entire Place or Private Room.";
    }
    if (!s.title.trim()) return "Please enter your property title.";
    if (!s.sales_price) return "Please enter the property price.";
    if (!s.checkin_time) return "Please select a check-in time.";
    if (!s.checkout_time) return "Please select a check-out time.";
    if (Number(s.room_number) < 1) return "Please add at least one room.";
    if (Number(s.beds) < 1) return "Please add at least one bed.";
    if (Number(s.baths) < 0.5) return "Please add at least 0.5 bathroom.";
    if (Number(s.adults) < 1) return "Please add at least one adult.";
    if (!s.real_address.trim()) return "Please enter the property address.";
    if (!s.content.trim()) return "Please describe your property.";
    if (!s.editMode && !s.feature_image) return "Please upload a feature image.";
    if (s.editMode && !s.feature_image && !s.imagePreview) return "Feature image is missing.";
    return null;
  };

  getWebUserId = () => {
    return (
      localStorage.getItem("web_user_id") ||
      sessionStorage.getItem("web_user_id") ||
      "1"
    );
  };

  handleSubmit = async () => {
    const validationError = this.validateListing();

    if (validationError) {
      this.setState({ errorMessage: validationError, submitSuccess: false });
      return;
    }

    const s = this.state;
    try {
      this.setState({
        submitting: true,
        errorMessage: "",
        submitSuccess: false,
      });
      const formData = new FormData();

      formData.append("customer_id", this.getWebUserId());
      formData.append("web_user_id", this.getWebUserId());
      if (s.editMode && s.editPostId) {
        formData.append("id", s.editPostId);
      }

      formData.append("category_id", s.category_id);
      formData.append("facility_ids", s.facility_ids);
      formData.append("property_type_id", s.property_type_id);
      formData.append("property_type", s.property_type_id);
      formData.append("title", s.title.trim());
      formData.append("content", s.content.trim());
      formData.append("sales_price", s.sales_price);
      formData.append("checkin_time", s.checkin_time);
      formData.append("checkout_time", s.checkout_time);
      formData.append("room_number", s.room_number);
      formData.append("beds", s.beds);
      formData.append("baths", s.baths);
      formData.append("adults", s.adults);
      formData.append("children", s.children);
      formData.append("real_address", s.real_address.trim());
      formData.append("map_latitude", s.map_latitude);
      formData.append("map_longitude", s.map_longitude);

      formData.append("room_type", s.property_type_id); 
      formData.append("minimum_night", s.minimum_night);
      formData.append("extra_charge_on_additional_guest_after", s.extra_charge_on_additional_guest_after);
      formData.append("per_guest_charge", s.per_guest_charge);
      formData.append("weekend_price", s.weekend_price);
      formData.append("booking_status", s.booking_status);
      formData.append("post_state", s.post_state);

      formData.append("more_night_discount", JSON.stringify(s.dynamic_discounts));

      if (s.feature_image) {
        formData.append("feature_image", s.feature_image);
      }
      s.gallery_images.forEach((file) => {
        formData.append("gallery_images[]", file);
      });

      const endpoint = s.editMode ? "/post_update" : "/post_create";
      await axios.post(endpoint, formData);

      this.setState({ submitting: false, submitSuccess: true, errorMessage: "" });
      await this.fetchProperties();

      setTimeout(() => {
        this.handleExit();
      }, 1500);
    } catch (error) {
      console.error("Listing save error:", error);
      const message =
        error?.response?.data?.message ||
        "Failed to save your listing. Please try again.";
      this.setState({
        submitting: false,
        errorMessage: message,
        submitSuccess: false,
      });
    }
  };

  /* =========================================
     RENDER: TABS & VIEWS 
  ========================================= */

  renderDashboard = () => {
    const { activeTab, properties, loadingProperties, showActionMenu } =
      this.state;

    const getStatusText = (state) => {
      if (String(state) === "1") return "Active";
      if (String(state) === "0") return "Request";
      if (String(state) === "2") return "Inactive";
      return "Unknown";
    };

    const filteredProperties =
      activeTab === "All"
        ? properties
        : properties.filter(
            (property) =>
              getStatusText(property.post_state).toLowerCase() ===
              activeTab.toLowerCase()
          );

    return (
      <div
        className="pb-5"
        style={{ marginBottom: "80px" }}
        onClick={() => this.setState({ showActionMenu: null })}
      >
        <div className="d-flex justify-content-between align-items-center p-3 pt-4 bg-white sticky-top z-1 mb-2">
          <h3 className="fw-bold mb-0">My Listing</h3>
          <button
            type="button"
            onClick={() => this.setState({ step: 1, errorMessage: "" })}
            className="btn text-white fw-bold d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm"
            style={{ backgroundColor: this.brandColor }}
          >
            <Plus size={18} /> Create
          </button>
        </div>

        <div
          className="d-flex gap-2 px-3 mb-4 overflow-x-auto"
          style={{ scrollbarWidth: "none" }}
        >
          {["All", "Active", "Request", "Inactive"].map((tab) => (
            <button
              type="button"
              key={tab}
              onClick={() => this.setState({ activeTab: tab })}
              className={`btn rounded-pill border px-3 py-1 fw-medium ${
                activeTab === tab
                  ? "text-white"
                  : "btn-light text-muted border-secondary-subtle"
              }`}
              style={{
                whiteSpace: "nowrap",
                fontSize: "14px",
                backgroundColor: activeTab === tab ? this.brandColor : "",
                borderColor: activeTab === tab ? this.brandColor : "",
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="px-3">
          {loadingProperties ? (
            <div className="text-center py-5">
              <Loader2
                size={30}
                className="listing-spin"
                color={this.brandColor}
              />
              <p className="text-muted mt-2">Loading your listings...</p>
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <Building size={42} className="mb-3 opacity-50" />
              <p className="mb-0">
                No {activeTab === "All" ? "" : activeTab.toLowerCase() + " "}
                properties found.
              </p>
            </div>
          ) : (
            filteredProperties.map((property) => {
              const image =
                property.feature_image || property.image || property.img;
              const imageUrl = image
                ? image.startsWith("http")
                  ? image
                  : `${this.backendUploadsPath}${image}`
                : "";

              const currentStatus = getStatusText(property.post_state);
              const identifier = property.id || property.post_id;

              return (
                <div
                  key={identifier}
                  className={`card rounded-4 overflow-hidden border-0 shadow-sm border border-light-subtle mb-4 ${
                    currentStatus === "Inactive" ? "opacity-75" : ""
                  }`}
                >
                  <div
                    className="position-relative bg-secondary bg-opacity-25"
                    style={{
                      height: "220px",
                      backgroundImage: imageUrl ? `url(${imageUrl})` : "none",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    <div
                      className="position-absolute bg-white rounded-circle d-flex align-items-center justify-content-center shadow"
                      style={{
                        top: "12px",
                        right: "12px",
                        width: "32px",
                        height: "32px",
                        cursor: "pointer",
                        zIndex: 10,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        this.setState((prev) => ({
                          showActionMenu:
                            prev.showActionMenu === identifier
                              ? null
                              : identifier,
                        }));
                      }}
                    >
                      <MoreVertical size={18} className="text-dark" />
                    </div>

                    {showActionMenu === identifier && (
                      <div
                        className="position-absolute bg-white shadow rounded-3 py-1"
                        style={{
                          top: "48px",
                          right: "12px",
                          zIndex: 20,
                          minWidth: "120px",
                        }}
                      >
                        <button
                          className="btn btn-link text-dark text-decoration-none w-100 text-start px-3 py-2 fw-medium"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.handleEditInit(identifier);
                          }}
                        >
                          ✏️ Edit Post
                        </button>
                      </div>
                    )}

                    {currentStatus !== "Unknown" && (
                      <div
                        className={`position-absolute bottom-0 end-0 text-white fw-bold px-3 py-1 listing-status ${
                          currentStatus === "Request"
                            ? "bg-warning text-dark"
                            : "bg-primary"
                        }`}
                      >
                        {currentStatus}
                      </div>
                    )}
                  </div>
                  <div className="card-body p-3">
                    <h6 className="fw-bold lh-sm pe-3 mb-1">
                      {property.title}
                    </h6>
                    <p className="text-muted small mb-2">
                      {property.location ||
                        property.real_address ||
                        "Location not available"}
                    </p>
                    <p className="fw-bold mb-0">
                      BDT {property.sales_price || property.price || "0"}
                      <span className="text-muted fw-normal small"> /day</span>
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  /* STATIC PLACE TYPE SELECTION */
  renderPlaceType = () => {
    const { property_type_id, errorMessage } = this.state;

    return (
      <div className="d-flex flex-column vh-100 bg-white">
        <div className="p-3 pt-4">
          <button
            type="button"
            onClick={this.handleExit}
            className="btn btn-outline-secondary rounded-pill btn-sm fw-medium px-4"
          >
            Exit
          </button>
        </div>
        <div className="px-4 flex-grow-1" style={{ paddingBottom: "160px" }}>
          <div className="mb-4">
            <h2 className="fw-bold mb-2 lh-sm">
              What type of place will guests have?
            </h2>
            <p className="text-muted small mb-0">Choose one option. This field is required.</p>
          </div>

          <div className="d-flex flex-column gap-3">
            <div
              onClick={() =>
                this.setState({
                  property_type_id: "Entire Place",
                  errorMessage: "",
                })
              }
              className={`card p-4 border-2 rounded-4 d-flex flex-row align-items-center justify-content-between transition-all cursor-pointer ${
                property_type_id === "Entire Place" ? "bg-light" : "border-light-subtle"
              }`}
              style={{
                borderColor: property_type_id === "Entire Place" ? this.brandColor : "",
              }}
            >
              <div className="pe-3">
                <h5 className="fw-bold mb-2">Entire Place</h5>
                <p className="text-muted small mb-0">
                  Guests have the whole place to themselves.
                </p>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={36}
                height={36}
                viewBox="0 0 24 24"
                fill="none"
                stroke={
                  property_type_id === "Entire Place"
                    ? this.brandColor
                    : "#222222"
                }
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-house flex-shrink-0"
              >
                <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
                <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              </svg>
            </div>

            <div
              onClick={() =>
                this.setState({
                  property_type_id: "Private Room",
                  errorMessage: "",
                })
              }
              className={`card p-4 border-2 rounded-4 d-flex flex-row align-items-center justify-content-between transition-all cursor-pointer ${
                property_type_id === "Private Room" ? "bg-light" : "border-light-subtle"
              }`}
              style={{
                borderColor: property_type_id === "Private Room" ? this.brandColor : "",
              }}
            >
              <div className="pe-3">
                <h5 className="fw-bold mb-2">Private Room</h5>
                <p className="text-muted small mb-0">
                  Guests have their own room in a home plus access to private
                  place.
                </p>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={36}
                height={36}
                viewBox="0 0 24 24"
                fill="none"
                stroke={
                  property_type_id === "Private Room"
                    ? this.brandColor
                    : "#222222"
                }
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-door-closed flex-shrink-0"
              >
                <path d="M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14" />
                <path d="M2 20h20" />
                <path d="M14 12v.01" />
              </svg>
            </div>
          </div>
        </div>

        {errorMessage && (
          <div className="px-4 pb-2 text-danger d-flex align-items-center gap-2 fw-bold place-type-error">
            <AlertCircle size={16} /> <span>{errorMessage}</span>
          </div>
        )}

        {this.renderBottomBarNav({
          onBack: () => this.setState({ step: 4 }),
          onNext: () => {
            if (!this.state.property_type_id || !["Entire Place", "Private Room"].includes(String(this.state.property_type_id))) {
              this.setState({
                errorMessage: "Please select Entire Place or Private Room.",
              });
              return;
            }
            this.setState({ errorMessage: "", step: 6 });
          },
          progressIndex: 3,
        })}
      </div>
    );
  };

  /* MOBILE DYNAMIC DISCOUNT BOTTOM SHEET */
  renderDiscountModal = () => {
    const { showDiscountPopup, dynamic_discounts } = this.state;

    if (!showDiscountPopup || typeof document === "undefined") return null;

    return createPortal(
      <div className="discount-modal-root" role="dialog" aria-modal="true" aria-labelledby="discountModalTitle">
        <button
          type="button"
          className="discount-modal-backdrop"
          aria-label="Close discount settings"
          onClick={() => this.setState({ showDiscountPopup: false })}
        />

        <section className="discount-sheet">
          <div className="discount-sheet-handle" aria-hidden="true" />

          <div className="discount-sheet-header">
            <button
              type="button"
              onClick={() => this.setState({ showDiscountPopup: false })}
              className="discount-sheet-back"
              aria-label="Back"
            >
              <ChevronLeft size={24} />
            </button>
            <div>
              <h5 id="discountModalTitle" className="fw-bold mb-1">Discount on more nights</h5>
              <p className="text-muted small mb-0">Offer a discount for longer stays.</p>
            </div>
          </div>

          <div className="discount-sheet-body">
            <div className="discount-info-card">
              <div className="fw-bold mb-1">Encourage longer stays</div>
              <div className="text-muted small">Set percentage discounts for customized duration or custom tags.</div>
            </div>

            {dynamic_discounts.map((discount, index) => (
              <div key={discount.id} className="discount-dynamic-item">
                <div className="discount-dynamic-header">
                  <div className="discount-dynamic-label-wrap">
                    <input
                      type="text"
                      className="discount-dynamic-label-input"
                      placeholder="Discount Name (e.g. Weekly)"
                      value={discount.label}
                      onChange={(e) =>
                        this.handleDiscountItemChange(discount.id, "label", e.target.value)
                      }
                    />
                  </div>
                  <button
                    type="button"
                    className="discount-dynamic-remove"
                    onClick={() => this.handleRemoveDiscount(discount.id)}
                    aria-label="Remove discount"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                
                <div className="discount-input-wrap">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    inputMode="decimal"
                    className="discount-input"
                    placeholder="Enter discount %"
                    value={discount.value}
                    onChange={(e) =>
                      this.handleDiscountItemChange(discount.id, "value", e.target.value)
                    }
                  />
                  <span>%</span>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="discount-add-btn"
              onClick={this.handleAddDiscount}
            >
              <Plus size={20} /> Add New Discount
            </button>

          </div>

          <div className="discount-sheet-footer">
            <button
              type="button"
              className="btn text-white w-100 py-3 rounded-pill fw-bold"
              style={{ backgroundColor: this.brandColor }}
              onClick={() => this.setState({ showDiscountPopup: false, errorMessage: "" })}
            >
              Save Changes
            </button>
          </div>
        </section>
      </div>,
      document.body
    );
  };

  /* ADD FACILITIES POPUP MODAL (MATCHING SCREENSHOT WITH PICK & REMOVE) */
  renderFacilityModal = () => {
    const { showFacilityPopup, facilities, facility_ids, facilitySearchQuery } = this.state;

    if (!showFacilityPopup || typeof document === "undefined") return null;

    const currentSelected = facility_ids
      ? facility_ids.split(",").map((s) => s.trim()).filter(Boolean)
      : [];

    const filteredFacilities = facilities.filter((f) => {
      const name = typeof f === "string" ? f : (f?.facility_name || f?.name || f?.value || "");
      return name.toLowerCase().includes((facilitySearchQuery || "").toLowerCase());
    });

    const handleToggleFacility = (facilityName) => {
      let updated = [...currentSelected];
      const index = updated.indexOf(facilityName);
      if (index > -1) {
        updated.splice(index, 1);
      } else {
        updated.push(facilityName);
      }
      const newFacilityIds = updated.join(",");
      this.setState({ facility_ids: newFacilityIds });
      if (this.facilityTagify) {
        this.facilityTagify.removeAllTags();
        if (updated.length > 0) {
          this.facilityTagify.addTags(updated);
        }
      }
    };

    return createPortal(
      <div className="facility-modal-root" role="dialog" aria-modal="true" aria-labelledby="facilityModalTitle">
        <button
          type="button"
          className="facility-modal-backdrop"
          aria-label="Close facilities"
          onClick={() => this.setState({ showFacilityPopup: false })}
        />

        <section className="facility-sheet">
          {/* Header */}
          <div className="facility-sheet-header">
            <button
              type="button"
              onClick={() => this.setState({ showFacilityPopup: false })}
              className="facility-sheet-back"
              aria-label="Back"
            >
              <ChevronLeft size={24} />
            </button>
            <h4 id="facilityModalTitle" className="fw-bold mb-0">Add Facilities</h4>
          </div>

          {/* Search Bar */}
          <div className="facility-sheet-search-wrap">
            <div className="facility-search-input-box">
              <Search size={18} className="text-muted" />
              <input
                type="text"
                className="facility-search-input"
                placeholder="Search facilities"
                value={facilitySearchQuery}
                onChange={(e) => this.setState({ facilitySearchQuery: e.target.value })}
              />
            </div>
          </div>

          {/* Facilities List */}
          <div className="facility-sheet-body">
            {filteredFacilities.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <Building size={36} className="mb-2 opacity-50" />
                <p className="mb-0">No facilities found.</p>
              </div>
            ) : (
              filteredFacilities.map((f, index) => {
                const name = typeof f === "string" ? f : (f?.facility_name || f?.name || f?.value || "");
                const isSelected = currentSelected.includes(name);

                return (
                  <div
                    key={f.id || index}
                    className="facility-item-row"
                    onClick={() => handleToggleFacility(name)}
                  >
                    <div className="facility-item-left">
                      <div className="facility-item-icon">
                        <Building size={20} style={{ color: this.brandColor }} />
                      </div>
                      <span className="facility-item-name">{name}</span>
                    </div>

                    <div className={`facility-checkbox ${isSelected ? "checked" : ""}`}>
                      {isSelected && <Check size={14} className="text-white" />}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer Save Button */}
          <div className="facility-sheet-footer">
            <button
              type="button"
              className="btn text-white w-100 py-3 rounded-pill fw-bold"
              style={{ backgroundColor: this.brandColor }}
              onClick={() => this.setState({ showFacilityPopup: false, facilitySearchQuery: "" })}
            >
              Save Changes
            </button>
          </div>
        </section>
      </div>,
      document.body
    );
  };

  /* DETAILS VIEW (WITH BASIC/ADVANCED TABS) */
  renderDetails = () => {
    const {
      formTab,
      title,
      sales_price,
      checkin_time,
      checkout_time,
      content,
      real_address,
      map_latitude,
      map_longitude,
      imagePreview,
      galleryPreviews,
      existingGallery,
      room_number,
      beds,
      baths,
      adults,
      children,
      minimum_night,
      extra_charge_on_additional_guest_after,
      per_guest_charge,
      weekend_price,
      booking_status,
      post_state,
      facility_ids,
      submitSuccess,
      errorMessage,
      submitting,
      editMode,
    } = this.state;

    return (
      <div className="my-listing-details-page d-flex flex-column bg-white">
        
        {/* Sticky Header Wrap */}
        <div className="my-listing-details-header bg-white border-bottom">
          <div className="d-flex align-items-center gap-3 p-3 pt-4">
            <button
              type="button"
              onClick={() => this.setState({ step: 5 })}
              className="btn p-0"
              disabled={submitting}
            >
              <ChevronLeft size={28} />
            </button>
            <h5 className="fw-bold mb-0">
              {editMode ? "Update Listing Details" : "Create Listing Details"}
            </h5>
          </div>

          {/* Tab Navigation */}
          <div className="d-flex w-100 px-2">
             <div 
               className={`flex-fill text-center py-3 fw-bold cursor-pointer transition-all ${formTab === 'Basic' ? 'border-bottom border-2' : 'text-muted'}`}
               style={{ color: formTab === 'Basic' ? this.brandColor : '', borderColor: formTab === 'Basic' ? this.brandColor : 'transparent' }}
               onClick={() => this.setState({ formTab: 'Basic', errorMessage: '' })}
             >
               Basic
             </div>
             <div 
               className={`flex-fill text-center py-3 fw-bold cursor-pointer transition-all ${formTab === 'Advanced' ? 'border-bottom border-2' : 'text-muted'}`}
               style={{ color: formTab === 'Advanced' ? this.brandColor : '', borderColor: formTab === 'Advanced' ? this.brandColor : 'transparent' }}
               onClick={() => this.setState({ formTab: 'Advanced', errorMessage: '' })}
             >
               Advanced
             </div>
          </div>
        </div>

        <div ref={this.detailsScrollRef} className="my-listing-details-scroll p-4">
          {submitSuccess && (
            <div className="alert alert-success d-flex align-items-center gap-2 rounded-4">
              <CheckCircle size={20} />{" "}
              {editMode
                ? "Listing updated successfully."
                : "Listing created successfully."}
            </div>
          )}
          {errorMessage && (
            <div className="alert alert-danger d-flex align-items-start gap-2 rounded-4">
              <AlertCircle size={20} className="flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* ---------------- BASIC TAB ---------------- */}
          {formTab === "Basic" && (
            <>
              {/* Feature Image Upload */}
              <input
                type="file"
                className="d-none"
                ref={this.fileInputRef}
                accept="image/*"
                onChange={this.handleFeatureImageChange}
              />
              {imagePreview ? (
                <div className="listing-feature-preview mb-4 position-relative">
                  <img
                    src={imagePreview}
                    alt="Feature"
                    className="img-fluid rounded-4 w-100"
                    style={{ height: "200px", objectFit: "cover" }}
                  />
                  <button
                    type="button"
                    className="btn btn-dark btn-sm position-absolute top-50 start-50 translate-middle shadow"
                    onClick={() => this.fileInputRef.current?.click()}
                  >
                    Change Photo
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => this.fileInputRef.current?.click()}
                  className="bg-light border rounded-4 d-flex flex-column align-items-center justify-content-center mb-4 transition-all hover-shadow"
                  style={{
                    width: "120px",
                    height: "120px",
                    borderStyle: "dashed",
                    borderWidth: "2px",
                    borderColor: this.brandColor,
                    color: this.brandColor,
                    cursor: "pointer",
                  }}
                >
                  <Camera size={32} className="mb-2" />
                  <span className="fw-bold" style={{ fontSize: "12px" }}>
                    Add Photos
                  </span>
                </div>
              )}

              {/* Gallery Upload */}
              <input
                type="file"
                multiple
                className="d-none"
                ref={this.galleryInputRef}
                accept="image/*"
                onChange={this.handleGalleryImagesChange}
              />
              <button
                type="button"
                className="btn btn-outline-primary rounded-pill mb-3"
                onClick={() => this.galleryInputRef.current?.click()}
              >
                <Camera size={17} className="me-2" />{" "}
                {editMode && existingGallery.length > 0
                  ? "Replace Gallery Photos"
                  : "Add Gallery Photos"}
              </button>

              {editMode && galleryPreviews.length > 0 && (
                <small className="text-warning fw-medium d-block mb-3">
                  Note: Uploading new files will replace your existing gallery
                  images.
                </small>
              )}

              {editMode &&
                existingGallery.length > 0 &&
                galleryPreviews.length === 0 && (
                  <div className="d-flex flex-wrap gap-2 mb-4">
                    {existingGallery.map((url, i) => (
                      <div key={i} style={{ width: "80px", height: "80px" }}>
                        <img
                          src={url}
                          alt={`Existing Gallery ${i + 1}`}
                          className="img-fluid rounded-3 w-100 h-100"
                          style={{ objectFit: "cover", opacity: 0.8 }}
                        />
                      </div>
                    ))}
                  </div>
                )}

              {galleryPreviews.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-4">
                  {galleryPreviews.map((image, index) => (
                    <div
                      key={index}
                      className="position-relative"
                      style={{ width: "80px", height: "80px" }}
                    >
                      <img
                        src={image.url}
                        alt={`Gallery draft ${index + 1}`}
                        className="img-fluid rounded-3 w-100 h-100"
                        style={{ objectFit: "cover" }}
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm p-0 rounded-circle position-absolute"
                        style={{
                          width: "20px",
                          height: "20px",
                          top: "-5px",
                          right: "-5px",
                          lineHeight: "1",
                        }}
                        onClick={() => this.removeGalleryImage(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Inputs */}
              <div className="mb-5 d-flex flex-column gap-3">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control rounded-3"
                    id="titleInput"
                    name="title"
                    placeholder="Title"
                    value={title}
                    onChange={this.handleChange}
                  />
                  <label htmlFor="titleInput" className="text-muted">
                    Listing Title
                  </label>
                </div>

                <div className="form-floating">
                  <input
                    type="number"
                    min="0"
                    className="form-control rounded-3"
                    id="rentInput"
                    name="sales_price"
                    placeholder="Rent per night"
                    value={sales_price}
                    onChange={this.handleChange}
                  />
                  <label htmlFor="rentInput" className="text-muted">
                    Rent per night (BDT)
                  </label>
                </div>

                <div className="row g-2">
                  <div className="col-6">
                    <div className="form-floating">
                      <input
                        type="time"
                        className="form-control rounded-3"
                        id="checkinInput"
                        name="checkin_time"
                        placeholder="Check-in Time"
                        value={checkin_time}
                        onChange={this.handleChange}
                      />
                      <label htmlFor="checkinInput" className="text-muted">
                        Check-in Time
                      </label>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-floating">
                      <input
                        type="time"
                        className="form-control rounded-3"
                        id="checkoutInput"
                        name="checkout_time"
                        placeholder="Check-out Time"
                        value={checkout_time}
                        onChange={this.handleChange}
                      />
                      <label htmlFor="checkoutInput" className="text-muted">
                        Check-out Time
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                {this.renderCounterRow({
                  label: "Rooms",
                  subLabel: "Total number of rooms",
                  value: room_number,
                  stateKey: "room_number",
                })}
                {this.renderCounterRow({
                  label: "Beds",
                  subLabel: "Total number of beds",
                  value: beds,
                  stateKey: "beds",
                })}
                {this.renderCounterRow({
                  label: "Bathrooms",
                  subLabel: "Total number of bathrooms",
                  value: baths,
                  stateKey: "baths",
                  step: 0.5,
                })}
                {this.renderCounterRow({
                  label: "Adults",
                  subLabel: "Maximum number of adults",
                  value: adults,
                  stateKey: "adults",
                })}
                {this.renderCounterRow({
                  label: "Children",
                  subLabel: "Ages 2-12",
                  value: children,
                  stateKey: "children",
                })}
              </div>

              <div className="form-floating mb-4">
                <textarea
                  className="form-control rounded-3"
                  id="addressInput"
                  name="real_address"
                  placeholder="Property address"
                  style={{ height: "100px" }}
                  value={real_address}
                  onChange={this.handleChange}
                />
                <label htmlFor="addressInput" className="text-muted">
                  Property Address
                </label>
              </div>

              <div className="listing-map-section mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-bold d-flex align-items-center gap-2">
                    <MapPin size={18} /> Property Location
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm fw-medium rounded-pill px-3"
                    onClick={this.getCurrentLocation}
                    style={{ backgroundColor: "transparent", border: `1px solid ${this.brandColor}`, color: this.brandColor }}
                  >
                    Use Current Location
                  </button>
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control rounded-3"
                        id="latitudeInput"
                        name="map_latitude"
                        placeholder="Latitude"
                        value={map_latitude}
                        onChange={this.handleChange}
                        disabled
                      />
                      <label htmlFor="latitudeInput">Latitude</label>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-floating">
                      <input
                        type="text"
                        className="form-control rounded-3"
                        id="longitudeInput"
                        name="map_longitude"
                        placeholder="Longitude"
                        value={map_longitude}
                        onChange={this.handleChange}
                        disabled
                      />
                      <label htmlFor="longitudeInput">Longitude</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4 form-floating">
                <textarea
                  className="form-control rounded-3"
                  id="descInput"
                  name="content"
                  placeholder="Describe your place..."
                  style={{ height: "140px" }}
                  value={content}
                  onChange={this.handleChange}
                />
                <label htmlFor="descInput" className="text-muted">
                  Describe your place...
                </label>
              </div>
              <br /><br /><br /><br /><br />
            </>
          )}

          {/* ---------------- ADVANCED TAB ---------------- */}
          {formTab === "Advanced" && (
            <div className="pt-2">
              <div className="mb-4">
                <h6 className="fw-bold mb-3 fs-5">Minimum Nights</h6>
                <input 
                  type="text" 
                  className="advanced-input" 
                  name="minimum_night" 
                  value={minimum_night} 
                  onChange={this.handleChange} 
                  placeholder="1" 
                />
              </div>

              <div className="mb-4">
                <h6 className="fw-bold mb-3 fs-5">Additional Guest</h6>
                <input 
                  type="text" 
                  className="advanced-input" 
                  name="extra_charge_on_additional_guest_after" 
                  value={extra_charge_on_additional_guest_after} 
                  onChange={this.handleChange} 
                  placeholder="Extra Charge on additional guest after" 
                />
                <input 
                  type="text" 
                  className="advanced-input" 
                  name="per_guest_charge" 
                  value={per_guest_charge} 
                  onChange={this.handleChange} 
                  placeholder="Per guest charge" 
                />
              </div>

              <div className="mb-4">
                <h6 className="fw-bold mb-3 fs-5">Weekend price</h6>
                <input 
                  type="text" 
                  className="advanced-input" 
                  name="weekend_price" 
                  value={weekend_price} 
                  onChange={this.handleChange} 
                  placeholder="Weekly price (Friday & Saturday)" 
                />
              </div>

              {/* Facilities Section with Clickable Field Opening Popup */}
              <div className="mb-4">
                <h6 className="fw-bold mb-2 fs-5">Facilities</h6>
                <div
                  onClick={() => this.setState({ showFacilityPopup: true })}
                  className="facility-field-trigger cursor-pointer"
                >
                  <input
                    type="text"
                    ref={this.facilitysInputRef}
                    defaultValue={facility_ids || ""}
                    className="advanced-input mb-0 cursor-pointer"
                    placeholder="Type & select facilities"
                    readOnly
                    onClick={(e) => {
                      e.stopPropagation();
                      this.setState({ showFacilityPopup: true });
                    }}
                  />
                </div>
              </div>

              <button 
                type="button" 
                className={`advanced-action-btn ${booking_status === "1" ? "active-state" : ""}`}
                onClick={() => this.setState({ booking_status: booking_status === "1" ? "0" : "1" })}
              >
                {booking_status === "1" ? "Auto Accept Booking (ON)" : "Auto Accept Booking"}
              </button>

              <button 
                type="button" 
                className="advanced-action-btn"
                onClick={() => this.setState({ showDiscountPopup: true })}
              >
                Discount On More Nights
              </button>

              <button 
                type="button" 
                className={`advanced-action-btn ${post_state === "2" ? "active-state" : ""}`}
                onClick={() => this.setState({ post_state: post_state === "2" ? "1" : "2" })}
              >
                {post_state === "2" ? "Mark As Active" : "Mark As Inactive"}
              </button>
              
              <br /><br /><br /><br />
            </div>
          )}
        </div>

        {/* Publish / Save Changes Button Layer */}
        <div className="my-listing-save-bar bg-white border-top px-4 py-3">
          <button
            type="button"
            onClick={this.handleSubmit}
            disabled={submitting}
            className="btn text-white w-100 py-3 rounded-pill fw-bold shadow-sm fs-5"
            style={{ backgroundColor: this.brandColor }}
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="me-2 listing-spin" />{" "}
                {editMode ? "Saving Changes..." : "Publishing..."}
              </>
            ) : editMode || formTab === "Advanced" ? (
              "Save Changes"
            ) : (
              "Publish Listing"
            )}
          </button>
        </div>
        
        {/* Modals */}
        {this.renderDiscountModal()}
        {this.renderFacilityModal()}

      </div>
    );
  };

  /* UTILITY RENDER HELPERS */
  renderBottomBarNav = ({
    onBack,
    onNext,
    nextText = "Next",
    progressIndex,
  }) => {
    const { submitting } = this.state;
    return (
      <div
        className="position-fixed bg-white border-top px-4 py-3 z-3 w-100"
        style={{
          ...this.containerStyle,
          left: "50%",
          transform: "translateX(-50%)",
          bottom: this.bottomNavOffset,
        }}
      >
        {progressIndex !== undefined && (
          <div className="d-flex gap-2 mb-3">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="w-100 rounded-pill"
                style={{
                  height: "6px",
                  backgroundColor:
                    item <= progressIndex ? this.brandColor : "#e9ecef",
                }}
              />
            ))}
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center">
          <button
            type="button"
            onClick={onBack}
            className="btn btn-link text-dark text-decoration-underline fw-bold px-0"
            disabled={submitting}
          >
            Back
          </button>
          <button
            type="button"
            onClick={onNext}
            className="btn text-white fw-bold px-5 py-2 rounded-pill shadow-sm"
            style={{ backgroundColor: this.brandColor }}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="me-2 listing-spin" /> Saving...
              </>
            ) : (
              nextText
            )}
          </button>
        </div>
      </div>
    );
  };

  renderCounterRow = ({ label, subLabel, value, stateKey, step = 1 }) => {
    let displayValue = value;
    let extraBadge = null;

    if (stateKey === "baths") {
      displayValue = Number(value).toFixed(1);
      if (Number(value) === 0.5) extraBadge = "Non Attach";
      else if (Number(value) === 1.0) extraBadge = "Attach";
    }

    return (
      <div className="d-flex align-items-center justify-content-between py-3 border-bottom">
        <div>
          <h6 className="fw-bold mb-1 d-flex align-items-center">
            {label}
            {extraBadge && (
              <span
                className="badge ms-2 fw-medium"
                style={{
                  backgroundColor: "#e2ebf7",
                  color: this.brandColor,
                  fontSize: "11px",
                }}
              >
                {extraBadge}
              </span>
            )}
          </h6>
          {subLabel && <small className="text-muted">{subLabel}</small>}
        </div>
        <div className="d-flex align-items-center gap-3">
          <button
            type="button"
            onClick={() => this.updateCounter(stateKey, -step)}
            className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center p-0"
            style={{ width: "32px", height: "32px" }}
          >
            -
          </button>
          <span className="fw-bold text-center" style={{ width: "24px" }}>
            {displayValue}
          </span>
          <button
            type="button"
            onClick={() => this.updateCounter(stateKey, step)}
            className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center p-0"
            style={{ width: "32px", height: "32px" }}
          >
            +
          </button>
        </div>
      </div>
    );
  };

  renderHostingIntro = () => (
    <div className="d-flex flex-column vh-100 position-relative">
      <div
        className="position-relative p-4"
        style={{
          height: "45%",
          backgroundImage:
            "url(https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&q=80&w=450&h=400)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-25"></div>
        <button
          type="button"
          onClick={this.handleExit}
          className="btn text-white p-0 position-relative z-3 mt-2 bg-dark bg-opacity-50 rounded-circle"
          style={{ width: "40px", height: "40px" }}
        >
          <ChevronLeft size={24} />
        </button>
      </div>
      <div
        className="flex-grow-1 bg-white p-4 pt-5 d-flex flex-column rounded-top-4"
        style={{ marginTop: "-20px", zIndex: 2 }}
      >
        <h2 className="fw-bold mb-3 lh-sm">
          Experience the world of hosting with Us
        </h2>
        <p className="text-muted mb-4 lh-lg">
          Got some extra space? Turn it into extra income by hosting with us.
        </p>
        <div
          className="mt-auto"
          style={{ marginBottom: this.bottomNavOffset }}
        >
          <button
            type="button"
            onClick={() => this.setState({ step: 2 })}
            className="btn text-white fw-bold w-100 py-3 rounded-pill shadow-sm fs-5"
            style={{ backgroundColor: this.brandColor }}
          >
            Get Started
          </button>
        </div>
      </div>
    </div>
  );

  renderStepsOverview = () => (
    <div className="d-flex flex-column vh-100 bg-white">
      <div className="p-3 pt-4">
        <button
          type="button"
          onClick={() => this.setState({ step: 1 })}
          className="btn p-0"
        >
          <ChevronLeft size={28} />
        </button>
      </div>
      <div className="px-4 flex-grow-1 overflow-y-auto pb-5">
        <h1 className="fw-bold mb-5 lh-sm">It's easy to get started</h1>
        <div className="d-flex flex-column gap-4">
          <div className="d-flex gap-3 align-items-start border-bottom pb-4">
            <div className="flex-grow-1">
              <h5 className="fw-bold mb-2">1. Tell us about your place</h5>
              <p className="text-muted small mb-0">Share some basic info.</p>
            </div>
          </div>
          <div className="d-flex gap-3 align-items-start border-bottom pb-4">
            <div className="flex-grow-1">
              <h5 className="fw-bold mb-2">2. Make it stand out</h5>
              <p className="text-muted small mb-0">Add 5 or more photos.</p>
            </div>
          </div>
          <div className="d-flex gap-3 align-items-start">
            <div className="flex-grow-1">
              <h5 className="fw-bold mb-2">3. Finish up and publish</h5>
              <p className="text-muted small mb-0">
                Set a starting price and publish.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div
        className="p-4 bg-white border-top position-fixed w-100"
        style={{
          ...this.containerStyle,
          left: "50%",
          transform: "translateX(-50%)",
          bottom: this.bottomNavOffset,
        }}
      >
        <button
          type="button"
          onClick={() => this.setState({ step: 3 })}
          className="btn text-white fw-bold w-100 py-3 rounded-pill fs-5"
          style={{ backgroundColor: this.brandColor }}
        >
          Continue
        </button>
      </div>
    </div>
  );

  renderStep1Intro = () => (
    <div className="d-flex flex-column vh-100 bg-white">
      <div className="p-3 pt-4">
        <button
          type="button"
          onClick={this.handleExit}
          className="btn btn-outline-secondary rounded-pill btn-sm fw-medium px-4"
        >
          Exit
        </button>
      </div>
      <div className="px-4 flex-grow-1 d-flex flex-column">
        <div
          className="w-100 rounded-4 mb-4"
          style={{
            height: "240px",
            backgroundImage:
              "url(https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&q=80&w=450&h=400)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <h5 className="fw-bold text-muted mb-2">Step 1</h5>
        <h1 className="fw-bold mb-3 lh-sm">Tell us about your place</h1>
        <p className="text-muted fs-5">
          In this step, we'll ask you which type of property you have and if
          guests will book the entire place or just a room.
        </p>
      </div>
      {this.renderBottomBarNav({
        onBack: () => this.setState({ step: 2 }),
        onNext: () => this.setState({ step: 4 }),
        progressIndex: 1,
      })}
    </div>
  );

  renderPropertyType = () => {
    const { categories, loadingCategories, category_id, errorMessage } = this.state;
    return (
      <div className="d-flex flex-column vh-100 bg-white">
        <div className="p-3 pt-4">
          <button type="button" onClick={this.handleExit} className="btn btn-outline-secondary rounded-pill btn-sm fw-medium px-4">Exit</button>
        </div>
        <div className="px-4 flex-grow-1 overflow-y-auto" style={{ paddingBottom: "160px" }}>
          <h2 className="fw-bold mb-4 lh-sm">Which of these best describes your property?</h2>
          {loadingCategories ? (
            <div className="text-center py-5">
              <Loader2 size={30} color={this.brandColor} className="listing-spin" />
            </div>
          ) : (
            <div className="row g-3">
              {categories.map((category, index) => {
                const selected = String(category_id) === String(category.id);
                return (
                  <div key={category.id || index} className="col-6">
                    <div
                      onClick={() => this.setState({ category_id: category.id, errorMessage: "" })}
                      className={`card h-100 p-4 border-2 rounded-4 cursor-pointer text-center d-flex align-items-start justify-content-center gap-3 transition-all ${selected ? "bg-light" : "border-light-subtle"}`}
                      style={{ borderColor: selected ? this.brandColor : "" }}
                    >
                      <Building size={32} color={selected ? this.brandColor : "#6c757d"} />
                      <span className="fw-bold text-dark">{category.category_name || category.name || "Property"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {errorMessage && (
          <div className="px-4 pb-2 text-danger d-flex align-items-center gap-2 fw-bold" style={{ marginBottom: "80px" }}>
            <AlertCircle size={16} /> {errorMessage}
          </div>
        )}
        {this.renderBottomBarNav({
          onBack: () => this.state.editMode ? this.handleExit() : this.setState({ step: 3 }),
          onNext: () => {
            if (!this.state.category_id) {
              this.setState({ errorMessage: "Please select a property category." });
              return;
            }
            this.setState({ errorMessage: "", step: 5 });
          },
          progressIndex: 2,
        })}
      </div>
    );
  };

  render() {
    const { step } = this.state;
    return (
      <div
        className="bg-white min-vh-100 position-relative mx-auto shadow-lg overflow-hidden"
        style={this.containerStyle}>
        {step === 0 && this.renderDashboard()}
        {step === 1 && this.renderHostingIntro()}
        {step === 2 && this.renderStepsOverview()}
        {step === 3 && this.renderStep1Intro()}
        {step === 4 && this.renderPropertyType()}
        {step === 5 && this.renderPlaceType()}
        {step === 6 && this.renderDetails()}
      </div>
    );
  }
}

export default MyListing;